import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import * as api from "../services/api";

const FREE_DAILY_LIMIT = 3;

const categoryColors: Record<string, string> = {
  finance: "bg-green-50 text-green-700 border-green-200",
  analytics: "bg-blue-50 text-blue-700 border-blue-200",
  marketing: "bg-orange-50 text-orange-700 border-orange-200",
  design: "bg-purple-50 text-purple-700 border-purple-200",
  developer: "bg-gray-50 text-gray-700 border-gray-200",
  productivity: "bg-teal-50 text-teal-700 border-teal-200",
};

const categoryLinks = [
  { name: "Finance", color: categoryColors.finance },
  { name: "Analytics", color: categoryColors.analytics },
  { name: "Marketing", color: categoryColors.marketing },
  { name: "Design", color: categoryColors.design },
  { name: "Developer", color: categoryColors.developer },
  { name: "Productivity", color: categoryColors.productivity },
];

function UsageBar({ used, limit }: { used: number; limit: number }) {
  const pct = Math.min((used / limit) * 100, 100);
  const color = pct >= 100 ? "bg-red-500" : pct >= 66 ? "bg-amber-400" : "bg-indigo-500";
  return (
    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
      <div className={color + " h-2 rounded-full transition-all"} style={{ width: pct + "%" }} />
    </div>
  );
}

export default function Dashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState<{ runs_total: number; runs_today: number; top_tool: string | null } | null>(null);
  const [recentRuns, setRecentRuns] = useState<any[]>([]);
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) return;
    setDataLoading(true);
    Promise.all([
      api.getMyStats().catch(() => null),
      api.getMyRuns(10).catch(() => ({ runs: [] })),
    ]).then(([s, r]) => {
      setStats(s);
      setRecentRuns(r?.runs || []);
    }).finally(() => setDataLoading(false));
  }, [user]);

  if (loading) return <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10 py-12"><p>Loading...</p></div>;
  if (!user) return null;

  const isFree = user.plan === "free";
  const runsToday = stats?.runs_today ?? 0;
  const runsTotal = stats?.runs_total ?? 0;
  const topTool = stats?.top_tool ?? null;

  return (
    <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10 py-12">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold">Dashboard</h1>
          <p className="text-gray-600">Welcome back, {user.name}!</p>
        </div>
        <span className={"px-3 py-1 rounded-full text-sm font-semibold " + (user.plan === "pro" ? "bg-indigo-100 text-indigo-800" : user.plan === "enterprise" ? "bg-purple-100 text-purple-800" : "bg-gray-100 text-gray-800")}>
          {user.plan.toUpperCase()} Plan
        </span>
      </div>

      {/* Analytics tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="border rounded-xl p-5 bg-white">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Total Runs</p>
          <p className="text-3xl font-bold text-gray-900">{dataLoading ? "—" : runsTotal.toLocaleString()}</p>
        </div>
        <div className="border rounded-xl p-5 bg-white">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Runs Today</p>
          <p className="text-3xl font-bold text-gray-900">{dataLoading ? "—" : runsToday}</p>
          {isFree && !dataLoading && (
            <div className="mt-2">
              <UsageBar used={runsToday} limit={FREE_DAILY_LIMIT} />
              <p className="text-xs text-gray-500 mt-1">{runsToday} / {FREE_DAILY_LIMIT} free runs used today</p>
            </div>
          )}
        </div>
        <div className="border rounded-xl p-5 bg-white">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Favourite Tool</p>
          <p className="text-lg font-bold text-gray-900 truncate">{dataLoading ? "—" : (topTool || "None yet")}</p>
          {topTool && (
            <Link to="/tools" className="text-xs text-indigo-600 hover:underline mt-1 inline-block">Browse tools →</Link>
          )}
        </div>
      </div>

      {/* Free plan upgrade nudge */}
      {isFree && !dataLoading && runsToday >= FREE_DAILY_LIMIT && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <p className="font-semibold text-amber-900">Daily limit reached</p>
            <p className="text-sm text-amber-700">You've used all {FREE_DAILY_LIMIT} free runs for today. Upgrade to Pro for unlimited access.</p>
          </div>
          <Link to="/billing" className="shrink-0 bg-amber-500 hover:bg-amber-600 text-white font-semibold px-5 py-2 rounded-lg text-sm transition">
            Upgrade Now
          </Link>
        </div>
      )}

      {/* Recent activity */}
      <section className="mb-10">
        <h2 className="text-xl font-bold mb-4">Recent Activity</h2>
        {dataLoading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : recentRuns.length === 0 ? (
          <div className="border rounded-lg px-4 py-3 text-sm text-gray-500">No recent activity yet. <Link to="/tools" className="text-indigo-600 hover:underline">Run a tool</Link> to get started!</div>
        ) : (
          <div className="border rounded-xl divide-y overflow-hidden">
            {recentRuns.map((run) => (
              <div key={run.id} className="flex items-center justify-between px-4 py-3 bg-white hover:bg-gray-50 text-sm">
                <div className="flex items-center gap-3 min-w-0">
                  <span className={"px-2 py-0.5 rounded-full text-xs font-semibold shrink-0 " + (run.status === "completed" ? "bg-green-100 text-green-800" : run.status === "failed" ? "bg-red-100 text-red-800" : "bg-gray-100 text-gray-700")}>
                    {run.status}
                  </span>
                  <Link to={"/tools/" + run.tool_slug} className="font-medium text-gray-800 hover:text-indigo-600 truncate">{run.tool_name}</Link>
                  <span className={"text-xs px-2 py-0.5 rounded-full border " + (categoryColors[run.tool_category] || "bg-gray-50 text-gray-500 border-gray-200")}>{run.tool_category}</span>
                </div>
                <div className="flex items-center gap-3 shrink-0 ml-4 text-gray-400">
                  {run.duration_ms != null && <span>{run.duration_ms}ms</span>}
                  <span>{new Date(run.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Category browse */}
      <section className="mb-10">
        <h2 className="text-xl font-bold mb-4">Browse by Category</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {categoryLinks.map((cat) => (
            <Link key={cat.name} to={"/tools?category=" + cat.name.toLowerCase()} className={"border rounded-xl p-6 hover:shadow-md transition " + cat.color}>
              <h3 className="font-semibold text-lg">{cat.name}</h3>
              <p className="text-sm mt-1 opacity-75">Browse tools &rarr;</p>
            </Link>
          ))}
        </div>
      </section>

      {/* Upgrade CTA for free users who haven't hit the limit yet */}
      {isFree && (runsToday < FREE_DAILY_LIMIT) && (
        <div className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl p-8 text-center">
          <h3 className="text-xl font-bold mb-2">Upgrade to Pro</h3>
          <p className="mb-4 text-indigo-100">Unlock unlimited runs, all 14 tools, and priority support.</p>
          <Link to="/pricing" className="bg-white text-indigo-600 font-semibold px-6 py-2 rounded-lg hover:bg-indigo-50 transition">View Plans</Link>
        </div>
      )}
    </div>
  );
}